/**
 * The double tick, as geometry, colour and words — one definition for the two places
 * that draw it.
 *
 * `<ReadReceipt>` renders it in our own conversation view; the Helper's
 * `content/skoolReceipts.ts` draws the same mark inside Skool's own DM window, where
 * there is no Svelte and no stylesheet of ours. They cannot share a component, so they
 * share this instead — otherwise the two ticks drift and the same fact looks like two
 * different features on two screens, which is exactly what happened.
 *
 * THE SECOND TICK IS ONLY ITS RISING STROKE. Drawn in full it crosses the first at a
 * shallow angle and the pair muddies into a zigzag by 13px, which is the size both
 * callers render at. Compared against three fuller geometries at 11/13/15/22/44px
 * before settling here.
 */

export const TICK_VIEWBOX = '0 0 16 12';

export const TICK_PATHS = ['M1.2 6.3 4.6 9.6 10.4 2.3', 'M8.4 9.6 14.2 2.3'] as const;

/**
 * The ticks on a LIGHT ground — Skool's own window, where the bubbles are white.
 *
 * ONE HUE, TWO GROUNDS. Our own bubble is solid `primary`, so a brand-blue tick there
 * is invisible against its own background; the panel therefore lifts the same hue to
 * `sky-300`. Rendering the two ends of that pair in different HUES is what made them
 * read as unrelated features — brand indigo here, pale cyan there. Measured side by
 * side, sky-500 on white and sky-300 on `primary` read as one colour adapted to its
 * ground, which is what consistency means when the grounds are opposite.
 *
 * A brand tint was tried first and fails for a different reason: on the primary bubble
 * every light tint of `#3452c4` collapses into the dimmed-white NOT-READ tick, and the
 * whole signal is the difference between those two.
 *
 * `sent` is the quiet half deliberately — it matches Skool's own timestamp grey, so an
 * unread message reads as "nothing has happened yet" rather than as a warning.
 */
export const TICK_INK = {
  read: '#0ea5e9',
  sent: '#909090',
} as const;

/**
 * What the ticks mean, spelled out for hover and for a screen reader.
 *
 * "Sent" alone is what this feature shipped with and it said nothing — it is true of
 * every message on screen. The state worth naming is the one where they have NOT caught
 * up, so that is the half the words carry, and it carries the wait with it: how long a
 * message has gone unread is the fact somebody actually acts on.
 *
 * THERE IS NO READ TIME TO SHOW unless a caller passes one, and today none can. Skool
 * reports whether a conversation is unread, never when it stopped being — `last_read_at`
 * looks like an answer and is the VIEWER'S OWN watermark. Recording the moment our
 * mirror first sees the counterpart drop out of the unread list would give a real one,
 * measured from our clock; until that exists this says "Read" and nothing more, because
 * "read 2 hours ago" would be a number we made up about somebody else's behaviour.
 */
export function receiptTitle(
  state: 'sent' | 'read',
  opts: { sentAt?: string | null; readAt?: string | null; now?: number } = {},
): string {
  const now = opts.now ?? Date.now();
  if (state === 'read') {
    const when = since(opts.readAt, now);
    return when ? `Read ${when}` : 'Read';
  }
  const waited = since(opts.sentAt, now);
  return waited ? `Not read yet · sent ${waited}` : 'Not read yet';
}

/**
 * "2 hours ago", "3 days ago" — the same scale `timeAgo` uses in the shared member
 * helpers, reimplemented in these few lines rather than imported.
 *
 * NOT A COPY MADE OUT OF LAZINESS. `member.ts` pulls in `./host`, which pulls the API
 * transport, and this module is imported by a CONTENT SCRIPT that runs on every Skool
 * page — a 3kB file has no business dragging the API client onto somebody else's site.
 * The scale is deliberately coarse: a receipt answers "recently or a while ago", and a
 * minute-accurate figure would invite the reader to trust a precision the underlying
 * poll does not have.
 */
function since(iso: string | null | undefined, now: number): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const mins = Math.floor(Math.max(0, now - t) / 60000);
  if (mins < 1) return 'just now';
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;
  if (mins < 60) return plural(mins, 'minute');
  const hours = Math.floor(mins / 60);
  if (hours < 24) return plural(hours, 'hour');
  const days = Math.floor(hours / 24);
  if (days < 30) return plural(days, 'day');
  const months = Math.floor(days / 30);
  if (months < 12) return plural(months, 'month');
  return plural(Math.floor(months / 12), 'year');
}
